import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useScrollReveal } from "../hooks/useScrollReveal";

const TermsOfServicePage = () => {
  const headerReveal = useScrollReveal({ threshold: 0.1 });
  const contentReveal = useScrollReveal({ threshold: 0.05 });

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-24">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
            headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            📜 Terms &amp; Conditions
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Last Updated: 22-04-2026
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-gray-50 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div
            ref={contentReveal.ref}
            className={`max-w-[1020px] mx-auto bg-white/95 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-soft ring-1 ring-gray-100 text-left transition-all duration-1000 ${
              contentReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <div className="pb-7 border-b border-gray-100">
              <p className="text-lg text-gray-600 leading-relaxed">
                Welcome to Domigo. By accessing our services, you agree to comply with the following Terms &amp; Conditions. Please read them carefully.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">1. Services</h2>
              <p className="text-lg text-gray-600 leading-relaxed">Domigo provides pet care services including boarding, daycare, grooming, and related services. All services are subject to availability and confirmation.</p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">2. Booking &amp; Onboarding</h2>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>All customers must complete the onboarding process before availing any service.</li>
                <li>Accurate and complete information about the pet must be provided.</li>
                <li>Domigo reserves the right to accept or reject any booking based on operational or safety considerations.</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">3. Pet Health &amp; Safety</h2>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Pets must be up-to-date with required vaccinations.</li>
                <li>Any medical conditions, behavioral issues, or special needs must be disclosed during onboarding.</li>
                <li>Domigo reserves the right to refuse admission if a pet is deemed unsafe for staff or other pets.</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">4. Customer Responsibilities</h2>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Provide accurate and truthful information at all times.</li>
                <li>Ensure timely drop-off and pick-up of pets.</li>
                <li>Provide necessary food, medication, or instructions if required.</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">5. Payments</h2>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>All services must be paid for as per the agreed terms.</li>
                <li>Pricing may vary depending on services, duration, and special requirements.</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">6. Liability</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">While Domigo takes all reasonable precautions, pet owners acknowledge that interactions between animals carry inherent risks.</p>
              <p className="text-lg text-gray-600 leading-relaxed">Domigo shall not be held liable for any illness, injury, or unforeseen incidents beyond reasonable control.</p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">7. Emergency Care</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">In case of a medical emergency, Domigo reserves the right to seek veterinary care.</p>
              <p className="text-lg text-gray-600 leading-relaxed">Any associated costs will be borne by the pet owner.</p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">8. Communication</h2>
              <p className="text-lg text-gray-600 leading-relaxed">By using our services, you consent to receive service-related communications including updates, reminders, and notifications.</p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">9. Modification of Services</h2>
              <p className="text-lg text-gray-600 leading-relaxed">Domigo reserves the right to modify, suspend, or discontinue any service at its discretion without prior notice.</p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">10. Governing Law</h2>
              <p className="text-lg text-gray-600 leading-relaxed">These Terms shall be governed by and interpreted in accordance with the laws of India.</p>
            </div>

            <div className="space-y-4 pt-7">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">11. Contact</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">For any queries, please contact:</p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo
                <br />
                Indore Bypass Road, near Kushabhau Thackrey Road,
                <br />
                Opposite Bharat Benz Showroom,
                <br />
                Indore, Madhya Pradesh – 452016
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;